import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Plus, Minus, Server, Boxes, Puzzle, Sparkles, Store, Network, Plug } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const INT_CATEGORIES = [
  { key: "ecommerce", label: "E-commerce & CMS" },
  { key: "corrieri", label: "Corrieri & Spedizioni" },
  { key: "erp", label: "ERP & Gestionali" },
  { key: "fatturazione", label: "Fatturazione" },
  { key: "fornitori", label: "Fornitori" },
  { key: "logistica", label: "Logistica & Magazzino" },
];

export default function Configurator() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [settings, setSettings] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [hostingId, setHostingId] = useState(null);
  const [moduleIds, setModuleIds] = useState(new Set());
  const [addonQty, setAddonQty] = useState({});
  const [bundleIds, setBundleIds] = useState(new Set());
  const [mpIds, setMpIds] = useState(new Set());
  const [intIds, setIntIds] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [agencyFinal, setAgencyFinal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/catalog");
      const activeOnly = {
        modules: data.modules.filter((x) => x.active),
        hosting: data.hosting.filter((x) => x.active),
        addons: data.addons.filter((x) => x.active),
        bundles: data.bundles.filter((x) => x.active),
        integrations: (data.integrations || []).filter((x) => x.active),
      };
      setCatalog(activeOnly);
      setSettings(data.settings || {});
      if (offerId) {
        const { data: o } = await api.get(`/offers/${offerId}`);
        setClientName(o.client_name || "");
        setClientCompany(o.client_company || "");
        setHostingId(o.hosting_plan?.id || null);
        setModuleIds(new Set((o.modules || []).map((m) => m.id)));
        const q = {};
        (o.addons || []).forEach((a) => (q[a.id] = a.quantity || 1));
        setAddonQty(q);
        setBundleIds(new Set((o.bundles || []).map((b) => b.id)));
        setMpIds(new Set((o.marketplaces || []).map((m) => m.id)));
        setIntIds(new Set((o.integrations || []).map((i) => i.id)));
        setNotes(o.notes || "");
        const af = o.agency_final_monthly ?? o.totals?.agency_final_monthly;
        setAgencyFinal(af != null ? String(af) : "");
      }
    })();
  }, [offerId]);

  const toggleSet = (setter, set, id) => {
    const n = new Set(set);
    n.has(id) ? n.delete(id) : n.add(id);
    setter(n);
  };

  const setQty = (id, delta) => {
    setAddonQty((prev) => {
      const cur = prev[id] || 0;
      const next = Math.max(0, cur + delta);
      const n = { ...prev };
      if (next === 0) delete n[id];
      else n[id] = next;
      return n;
    });
  };

  const intPrice = (it) => {
    if (it.price) return it.price;
    const t = it.integration_type || "standard";
    if (t === "custom") return settings.integration_custom_price || 500;
    if (t === "included") return 0;
    return settings.integration_standard_price || 399;
  };

  const partnerPct = (item) => {
    const v = item.partner_discount_percent;
    if (v !== null && v !== undefined && v !== "") return Number(v) || 0;
    const ap = user?.partner_discount_percent;
    if (ap !== null && ap !== undefined && ap !== "") return Number(ap) || 0;
    return Number(settings.partner_discount_percent) || 0;
  };
  const partnerPrice = (price, item) => (price || 0) * (1 - partnerPct(item) / 100);
  const bundleDiscPct = (b) => Math.max(0, Math.min(100, Number(b.bundle_discount_percent) || 0));
  const bundlePrice = (b) => (b.price || 0) * (1 - bundleDiscPct(b) / 100);
  const partnerHint = (price, item) => {
    if (!partnerPct(item)) return null;
    return (
      <p className="text-xs font-semibold text-primary mt-0.5" data-testid="partner-price-hint">
        Partner: {euro(partnerPrice(price, item))} <span className="text-muted-foreground font-normal">(−{partnerPct(item)}%)</span>
      </p>
    );
  };

  const selection = useMemo(() => {
    if (!catalog) return { modules: [], hosting: null, addons: [], bundles: [], marketplaces: [], integrations: [] };
    const modules = catalog.modules.filter((m) => moduleIds.has(m.id));
    const hosting = catalog.hosting.find((h) => h.id === hostingId) || null;
    const addons = catalog.addons
      .filter((a) => addonQty[a.id])
      .map((a) => ({ ...a, quantity: addonQty[a.id] }));
    const bundles = catalog.bundles.filter((b) => bundleIds.has(b.id));
    const marketplaces = (catalog.integrations || []).filter((i) => i.category === "marketplace" && mpIds.has(i.id));
    const integrations = (catalog.integrations || []).filter((i) => i.category !== "marketplace" && intIds.has(i.id));
    return { modules, hosting, addons, bundles, marketplaces, integrations };
  }, [catalog, moduleIds, hostingId, addonQty, bundleIds, mpIds, intIds]);

  const moduleDiscountPercent = (count) => {
    let p = 0;
    (settings.module_discount_tiers || []).slice().sort((a, b) => a.min_modules - b.min_modules)
      .forEach((t) => { if (count >= t.min_modules) p = t.percent; });
    return p;
  };

  const totals = useMemo(() => {
    let monthly = 0, oneoff = 0, moduleSubtotal = 0, partnerMonthly = 0, partnerOneoff = 0;
    const gp = Number(settings.partner_discount_percent) || 0;
    selection.modules.forEach((m) => { moduleSubtotal += m.price; });
    monthly += moduleSubtotal;
    const discPercent = moduleDiscountPercent(selection.modules.length);
    const moduleFactor = 1 - discPercent / 100;
    selection.modules.forEach((m) => { partnerMonthly += m.price * moduleFactor * (1 - partnerPct(m) / 100); });
    if (selection.hosting && selection.hosting.price_type !== "onrequest") {
      monthly += selection.hosting.price;
      partnerMonthly += selection.hosting.price * (1 - partnerPct(selection.hosting) / 100);
    }
    selection.addons.forEach((a) => {
      const cp = a.price * a.quantity;
      const pf = 1 - partnerPct(a) / 100;
      if (a.price_type === "oneoff") { oneoff += cp; partnerOneoff += cp * pf; }
      else { monthly += cp; partnerMonthly += cp * pf; }
    });
    let bundleDiscountTotal = 0;
    selection.bundles.forEach((b) => {
      const base = b.price || 0;
      const cp = bundlePrice(b);
      bundleDiscountTotal += base - cp;
      const pf = 1 - partnerPct(b) / 100;
      if (b.price_type === "oneoff") { oneoff += cp; partnerOneoff += cp * pf; }
      else { monthly += cp; partnerMonthly += cp * pf; }
    });
    const extraPrice = settings.marketplace_extra_price || 119;
    const mpExtraCount = Math.max(0, selection.marketplaces.length - 1);
    const mpCost = mpExtraCount * extraPrice;
    monthly += mpCost;
    partnerMonthly += mpCost * (1 - partnerPct({}) / 100);
    let intCost = 0;
    selection.integrations.forEach((it) => { const cp = intPrice(it); intCost += cp; partnerOneoff += cp * (1 - partnerPct(it) / 100); });
    oneoff += intCost;
    const moduleDiscount = moduleSubtotal * discPercent / 100;
    const monthlyNet = monthly - moduleDiscount;
    let monthlyFinal = monthlyNet, agencyDiscount = 0;
    const afm = agencyFinal === "" ? null : Number(agencyFinal);
    if (afm !== null && !isNaN(afm) && afm >= 0 && afm < monthlyNet) { monthlyFinal = afm; agencyDiscount = monthlyNet - afm; }
    const marginMonthly = monthlyFinal - partnerMonthly;
    const marginOneoff = oneoff - partnerOneoff;
    return { monthly, oneoff, monthlyNet, monthlyFinal, agencyDiscount, oneoffNet: oneoff, mpExtraCount, mpCost, intCost, extraPrice, discPercent, moduleDiscount, bundleDiscountTotal, partnerMonthly, partnerOneoff, marginMonthly, marginOneoff };
  }, [selection, settings, agencyFinal, user]);

  const save = async () => {
    if (!clientName.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    setSaving(true);
    const ref = (x, extra = {}) => ({ id: x.id, name: x.name, price: x.price, price_note: x.price_note, price_type: x.price_type, partner_discount_percent: (x.partner_discount_percent ?? null), bundle_discount_percent: (Number(x.bundle_discount_percent) || 0), ...extra });
    const payload = {
      client_name: clientName,
      client_company: clientCompany,
      modules: selection.modules.map((m) => ref(m)),
      hosting_plan: selection.hosting ? ref(selection.hosting) : null,
      addons: selection.addons.map((a) => ref(a, { quantity: a.quantity })),
      bundles: selection.bundles.map((b) => ref(b)),
      marketplaces: selection.marketplaces.map((m) => ({ id: m.id, name: m.name, category: "marketplace", logo: m.logo || "", price: 0, price_type: "monthly", partner_discount_percent: (m.partner_discount_percent ?? null) })),
      integrations: selection.integrations.map((i) => ({ id: i.id, name: i.name, category: i.category, integration_type: i.integration_type || "standard", logo: i.logo || "", price_type: "oneoff", partner_discount_percent: (i.partner_discount_percent ?? null) })),
      notes,
      status: "sent",
      agency_final_monthly: agencyFinal === "" ? null : Number(agencyFinal),
    };
    try {
      const res = offerId
        ? await api.put(`/offers/${offerId}`, payload)
        : await api.post("/offers", payload);
      toast.success("Offerta salvata");
      navigate(`/offerte/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) return <div className="p-10 text-muted-foreground">Caricamento configuratore...</div>;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <h1 className="text-3xl font-extrabold text-navy tracking-tight mb-1">
        {offerId ? "Modifica offerta" : "Configuratore offerta"}
      </h1>
      <p className="text-muted-foreground mb-8">Seleziona licenza, moduli e add-on per costruire l'offerta.</p>

      <div className="grid lg:grid-cols-3 gap-8 items-start">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-10">
          {/* Client */}
          <section className="bg-white border border-border rounded-xl p-6">
            <h2 className="font-extrabold text-navy mb-4">Dati cliente</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client">Nome cliente *</Label>
                <Input id="client" value={clientName} data-testid="client-name-input"
                  onChange={(e) => setClientName(e.target.value)} placeholder="Es. Mario Rossi" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="company">Azienda</Label>
                <Input id="company" value={clientCompany} data-testid="client-company-input"
                  onChange={(e) => setClientCompany(e.target.value)} placeholder="Es. Rossi Srl" className="mt-1.5" />
              </div>
            </div>
          </section>

          {/* Hosting */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Server size={18} className="text-primary" />
              <h2 className="font-extrabold text-navy">Licenza Hosting BSpanel</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {catalog.hosting.map((h) => {
                const active = hostingId === h.id;
                return (
                  <button key={h.id} type="button" data-testid={`hosting-${h.id}`}
                    onClick={() => setHostingId(active ? null : h.id)}
                    className={`text-left p-5 rounded-xl border transition-colors duration-200 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-primary/50"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-navy">{h.name}</span>
                      {active && <Check size={18} className="text-primary" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{h.volume}</p>
                    <p className="text-2xl font-extrabold text-navy mt-3">
                      {h.price_type === "onrequest" ? <span className="text-accent text-lg">{h.price_note || "Su richiesta"}</span> : <>{euro(h.price)}<span className="text-sm text-muted-foreground font-semibold">/mese</span></>}
                    </p>
                    {h.price_type !== "onrequest" && partnerHint(h.price, h)}
                    <p className="text-xs text-muted-foreground mt-2">{h.includes}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Marketplace */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <Store size={18} className="text-primary" />
              <h2 className="font-extrabold text-navy">Marketplace</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Il primo marketplace e incluso. Ogni marketplace aggiuntivo +{euro(settings.marketplace_extra_price || 119)}/mese.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {catalog.integrations.filter((i) => i.category === "marketplace").map((mp) => {
                const active = mpIds.has(mp.id);
                return (
                  <button key={mp.id} type="button" data-testid={`marketplace-${mp.id}`}
                    onClick={() => toggleSet(setMpIds, mpIds, mp.id)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-colors duration-200 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-primary/50"}`}>
                    {mp.logo ? <img src={mp.logo} alt="" className="h-6 w-6 object-contain shrink-0" /> : <Store size={16} className="text-muted-foreground shrink-0" />}
                    <span className="font-bold text-navy text-sm truncate flex-1">{mp.name}</span>
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-border"}`}>{active && <Check size={12} className="text-white" />}</div>
                  </button>
                );
              })}
            </div>
            {totals.mpExtraCount > 0 && (
              <p className="text-sm font-semibold text-navy mt-3" data-testid="marketplace-extra-note">
                {totals.mpExtraCount} marketplace {totals.mpExtraCount === 1 ? "aggiuntivo" : "aggiuntivi"} = {euro(totals.mpCost)}/mese
              </p>
            )}
          </section>

          {/* Modules */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Boxes size={18} className="text-primary" />
              <h2 className="font-extrabold text-navy">Moduli piattaforma</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {catalog.modules.map((m) => {
                const active = moduleIds.has(m.id);
                return (
                  <button key={m.id} type="button" data-testid={`module-${m.id}`}
                    onClick={() => toggleSet(setModuleIds, moduleIds, m.id)}
                    className={`text-left p-5 rounded-xl border transition-colors duration-200 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-primary/50"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-navy leading-tight">{m.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-border"}`}>
                        {active && <Check size={14} className="text-white" />}
                      </div>
                    </div>
                    <p className="text-lg font-extrabold text-navy mt-3">
                      {euro(m.price)}<span className="text-xs text-muted-foreground font-semibold">/mese</span>
                      {m.price_note && <span className="text-xs text-accent font-semibold ml-1">{m.price_note}</span>}
                    </p>
                    {partnerHint(m.price, m)}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Bundles */}
          {catalog.bundles.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-primary" />
                <h2 className="font-extrabold text-navy">Bundle</h2>
              </div>
              <div className="space-y-4">
                {catalog.bundles.map((b) => {
                  const active = bundleIds.has(b.id);
                  return (
                    <button key={b.id} type="button" data-testid={`bundle-${b.id}`}
                      onClick={() => toggleSet(setBundleIds, bundleIds, b.id)}
                      className={`w-full text-left p-5 rounded-xl border transition-colors duration-200 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-primary/50"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-extrabold text-navy">{b.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                        </div>
                        <div className="text-right shrink-0">
                          {bundleDiscPct(b) > 0 ? (
                            <>
                              <p className="text-sm text-muted-foreground line-through">{euro(b.price)}/mese</p>
                              <p className="text-lg font-extrabold text-navy">{euro(bundlePrice(b))}<span className="text-xs text-muted-foreground font-semibold">/mese</span></p>
                              <span className="inline-block text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5 mt-0.5">−{bundleDiscPct(b)}%</span>
                            </>
                          ) : (
                            <p className="text-lg font-extrabold text-navy">{euro(b.price)}<span className="text-xs text-muted-foreground font-semibold">/mese</span></p>
                          )}
                          {partnerHint(bundlePrice(b), b)}
                          {active && <Check size={16} className="text-primary inline mt-1" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Add-ons */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Puzzle size={18} className="text-primary" />
              <h2 className="font-extrabold text-navy">Add-on & integrazioni</h2>
            </div>
            <div className="space-y-3">
              {catalog.addons.map((a) => {
                const qty = addonQty[a.id] || 0;
                const active = qty > 0;
                return (
                  <div key={a.id} data-testid={`addon-${a.id}`}
                    className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors duration-200 ${active ? "border-primary bg-primary/5" : "border-border bg-white"}`}>
                    <div className="min-w-0">
                      <p className="font-bold text-navy">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.description}</p>
                      <p className="text-sm font-extrabold text-navy mt-1">
                        {a.price_type === "oneoff" && !a.price ? <span className="text-accent">{a.price_note}</span> : <>{euro(a.price)}<span className="text-xs text-muted-foreground font-semibold">{a.price_type === "oneoff" ? " una tantum" : "/mese"}</span></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => setQty(a.id, -1)} data-testid={`addon-minus-${a.id}`}
                        className="w-8 h-8 rounded-md border border-border flex items-center justify-center text-navy hover:bg-secondary transition-colors duration-200 disabled:opacity-40" disabled={qty === 0}>
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center font-bold text-navy" data-testid={`addon-qty-${a.id}`}>{qty}</span>
                      <button type="button" onClick={() => setQty(a.id, 1)} data-testid={`addon-plus-${a.id}`}
                        className="w-8 h-8 rounded-md border border-border flex items-center justify-center text-navy hover:bg-secondary transition-colors duration-200">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Integrazioni */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <Network size={18} className="text-primary" />
              <h2 className="font-extrabold text-navy">Integrazioni</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Ogni integrazione selezionata si somma al costo una tantum (Standard {euro(settings.integration_standard_price || 399)}, Custom da {euro(settings.integration_custom_price || 500)}).
            </p>
            {INT_CATEGORIES.map((cat) => {
              const items = catalog.integrations.filter((i) => i.category === cat.key);
              if (items.length === 0) return null;
              return (
                <div key={cat.key} className="mb-5">
                  <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">{cat.label}</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {items.map((it) => {
                      const active = intIds.has(it.id);
                      const custom = it.integration_type === "custom";
                      return (
                        <button key={it.id} type="button" data-testid={`integration-${it.id}`}
                          onClick={() => toggleSet(setIntIds, intIds, it.id)}
                          className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-colors duration-200 ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-primary/50"}`}>
                          {it.logo ? <img src={it.logo} alt="" className="h-6 w-6 object-contain shrink-0" /> : <Plug size={16} className="text-muted-foreground shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-navy text-sm truncate">{it.name}</p>
                            <p className="text-xs text-muted-foreground">{custom ? "Custom" : "Standard"} · {euro(intPrice(it))} una tantum</p>
                            {partnerHint(intPrice(it), it)}
                          </div>
                          <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-border"}`}>{active && <Check size={12} className="text-white" />}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {totals.intCost > 0 && (
              <p className="text-sm font-semibold text-navy" data-testid="integration-total-note">
                Totale integrazioni: {euro(totals.intCost)} una tantum
              </p>
            )}
          </section>
        </div>

        {/* RIGHT - summary */}
        <div className="lg:sticky lg:top-6">
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <div className="bg-navy text-white px-6 py-4">
              <p className="font-extrabold">Riepilogo offerta</p>
              <p className="text-white/60 text-sm">{clientName || "Nuovo cliente"}</p>
            </div>
            <div className="p-6 space-y-3 max-h-[320px] overflow-auto">
              {selection.hosting && <SummaryLine name={`Hosting ${selection.hosting.name}`} value={selection.hosting.price_type === "onrequest" ? "Su richiesta" : euro(selection.hosting.price) + "/mese"} />}
              {selection.modules.map((m) => <SummaryLine key={m.id} name={m.name} value={euro(m.price) + "/mese"} />)}
              {selection.bundles.map((b) => <SummaryLine key={b.id} name={b.name} value={euro(bundlePrice(b)) + "/mese"} />)}
              {selection.addons.map((a) => <SummaryLine key={a.id} name={`${a.name}${a.quantity > 1 ? " ×" + a.quantity : ""}`} value={a.price ? euro(a.price * a.quantity) + (a.price_type === "oneoff" ? "" : "/mese") : a.price_note} />)}
              {selection.marketplaces.length > 0 && <SummaryLine name={`Marketplace (${selection.marketplaces.length})`} value={totals.mpCost ? euro(totals.mpCost) + "/mese" : "incluso"} />}
              {selection.integrations.map((it) => <SummaryLine key={it.id} name={it.name} value={euro(intPrice(it))} />)}
              {selection.modules.length === 0 && !selection.hosting && selection.addons.length === 0 && selection.bundles.length === 0 && selection.marketplaces.length === 0 && selection.integrations.length === 0 && (
                <p className="text-sm text-muted-foreground">Nessun elemento selezionato.</p>
              )}
            </div>
            <div className="p-6 border-t border-border space-y-4">
              {totals.discPercent > 0 && (
                <div className="flex justify-between items-baseline bg-primary/5 border border-primary/20 rounded-lg px-3 py-2" data-testid="module-discount-line">
                  <span className="text-sm font-semibold text-primary">Sconto moduli {totals.discPercent}%</span>
                  <span className="text-sm font-extrabold text-primary">− {euro(totals.moduleDiscount)}</span>
                </div>
              )}
              <div>
                <Label htmlFor="notes">Note</Label>
                <Textarea id="notes" value={notes} data-testid="notes-input"
                  onChange={(e) => setNotes(e.target.value)} placeholder="Note per il cliente..." className="mt-1.5" rows={2} />
              </div>

              <div>
                <Label htmlFor="agency-final">Prezzo finale al cliente (mensile)</Label>
                <Input id="agency-final" type="number" value={agencyFinal} data-testid="agency-final-input"
                  onChange={(e) => setAgencyFinal(e.target.value)}
                  placeholder={`Listino: ${euro(totals.monthlyNet)}`} className="mt-1.5" />
                <p className="text-xs text-muted-foreground mt-1">Lascia vuoto per il prezzo di listino. Inserendo un importo inferiore applichi uno sconto che si accolla la tua agenzia.</p>
              </div>

              {totals.agencyDiscount > 0 && (
                <div className="space-y-1" data-testid="agency-discount-block">
                  <div className="flex justify-between items-baseline text-sm">
                    <span className="text-muted-foreground">Subtotale mensile</span>
                    <span className="font-semibold text-muted-foreground line-through">{euro(totals.monthlyNet)}</span>
                  </div>
                  <div className="flex justify-between items-baseline bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
                    <span className="text-sm font-semibold text-primary">Sconto agenzia</span>
                    <span className="text-sm font-extrabold text-primary" data-testid="agency-discount-amount">− {euro(totals.agencyDiscount)}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-baseline">
                <span className="text-muted-foreground text-sm">Totale mensile cliente</span>
                <span className="text-2xl font-extrabold text-navy" data-testid="summary-monthly-total">
                  {euro(totals.monthlyFinal)}<span className="text-sm text-muted-foreground font-semibold">/mese</span>
                </span>
              </div>
              {totals.oneoffNet > 0 && (
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground text-sm">Una tantum</span>
                  <span className="text-lg font-extrabold text-navy" data-testid="summary-oneoff-total">{euro(totals.oneoffNet)}</span>
                </div>
              )}

              {(totals.monthlyNet > 0 || totals.oneoffNet > 0) && (
                <div className="rounded-lg border border-dashed border-navy/30 bg-secondary/60 px-4 py-3 space-y-1.5" data-testid="agency-margin-box">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Riservato all'agenzia · non visibile al cliente</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Prezzo partner (a Bonsai) mensile</span>
                    <span className="font-bold text-navy" data-testid="partner-monthly">{euro(totals.partnerMonthly)}/mese</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Margine agenzia mensile</span>
                    <span className={`font-extrabold ${totals.marginMonthly < 0 ? "text-destructive" : "text-primary"}`} data-testid="margin-monthly">{euro(totals.marginMonthly)}/mese</span>
                  </div>
                  {totals.oneoffNet > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Margine agenzia una tantum</span>
                      <span className={`font-bold ${totals.marginOneoff < 0 ? "text-destructive" : "text-primary"}`}>{euro(totals.marginOneoff)}</span>
                    </div>
                  )}
                  {totals.marginMonthly < 0 && <p className="text-xs text-destructive font-semibold">Attenzione: il prezzo finale è sotto il costo partner.</p>}
                </div>
              )}

              <Button onClick={save} disabled={saving} data-testid="save-offer-button" className="w-full h-12 text-base font-bold">
                {saving ? "Salvataggio..." : offerId ? "Aggiorna offerta" : "Genera offerta"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryLine({ name, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-navy font-semibold truncate">{name}</span>
      <span className="text-muted-foreground whitespace-nowrap">{value}</span>
    </div>
  );
}
